import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, CreditCard, Clock } from "lucide-react";
import { http, formatApiError } from "@/lib/api";
import { naira } from "@/components/site/ShortletCard";
import { usePageMeta } from "@/hooks/useData";

const STATUS_TEXT = {
  pending: ["Waiting for approval", "We're checking your dates. You'll get an email with a payment link once they're approved."],
  approved: ["Approved — pay to confirm", "Your dates are held for you. Complete payment before the deadline to confirm your stay."],
  paid: ["Booking confirmed", "Thank you. Your payment was received and your stay is confirmed. We've emailed you the details."],
  declined: ["Request not approved", "Sorry, those dates weren't available. Contact us and we'll suggest alternatives."],
  cancelled: ["Booking cancelled", "This booking was cancelled. Contact us if you have any questions."],
  expired: ["Payment window closed", "The 48-hour hold on these dates has ended. Contact us to check availability again."],
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Confirms a returning Stripe (session_id) or Paystack (reference) payment, then reloads the booking.
const useReturnCheck = (reload) => {
  const [params] = useSearchParams();
  const [checking, setChecking] = useState(false);
  useEffect(() => {
    const sid = params.get("session_id");
    const ref = params.get("reference") || params.get("trxref");
    if (!sid && !ref) return undefined;
    let alive = true;
    const run = async () => {
      setChecking(true);
      for (let i = 0; i < 10 && alive; i += 1) {
        try {
          const r = sid ? await http.get(`/payments/status/${sid}`) : await http.get(`/paystack/verify/${ref}`);
          if (r.data?.payment_status === "paid") break;
          if (["expired", "failed", "abandoned"].includes(r.data?.payment_status)) break;
        } catch { break; }
        await sleep(2000);
      }
      if (alive) { setChecking(false); reload(); }
    };
    run();
    return () => { alive = false; };
  }, [params, reload]);
  return checking;
};

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-4 py-3 border-b border-gw-rule/20"><dt className="lab opacity-70">{k}</dt><dd className="text-right">{v}</dd></div>
);

const PayButtons = ({ token, providers }) => {
  const [busy, setBusy] = useState("");
  const pay = async (provider) => {
    setBusy(provider);
    try {
      const r = await http.post(`/bookings/pay/${token}/${provider}`, { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy("");
    }
  };
  const cls = "lab px-6 py-4 inline-flex justify-center items-center gap-2 transition-colors duration-200 disabled:opacity-60";
  return (
    <div className="grid sm:grid-cols-2 gap-3 mt-8" data-testid="booking-pay-buttons">
      <button type="button" disabled={!!busy} onClick={() => pay("stripe")} data-testid="booking-pay-stripe-button" className={`${cls} bg-gw-btn text-gw-btnfg hover:bg-gw-btnhover`}>
        {busy === "stripe" ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />}Pay by card
      </button>
      {providers?.paystack && (
        <button type="button" disabled={!!busy} onClick={() => pay("paystack")} data-testid="booking-pay-paystack-button" className={`${cls} border border-gw-rule/50 hover:bg-gw-hover`}>
          {busy === "paystack" && <Loader2 size={14} className="animate-spin" />}Pay with Paystack
        </button>
      )}
    </div>
  );
};

export default function BookingPay() {
  usePageMeta("Your shortlet booking | Wolfe Realtors", "Check the status of your Wolfe Realtors shortlet booking and pay securely.");
  const { token } = useParams();
  const [b, setB] = useState(undefined);
  const load = useCallback(() => http.get(`/bookings/pay/${token}`).then((r) => setB(r.data)).catch(() => setB(null)), [token]);
  useEffect(() => { load(); }, [load]);
  const checking = useReturnCheck(load);

  const wrap = "bg-gw-paper text-gw-ink min-h-[70vh] py-20 md:py-28 border-t-8 border-gw-rule";
  if (b === undefined) return <section className={wrap} />;
  if (b === null)
    return (
      <section className={wrap} data-testid="booking-not-found">
        <div className="max-w-xl mx-auto px-6"><h1 className="lux gw-h2">We couldn't find that booking.</h1>
          <Link to="/shortlets" className="lab mt-8 inline-block underline underline-offset-4">← Shortlets</Link></div>
      </section>
    );
  const [title, text] = STATUS_TEXT[b.status] || STATUS_TEXT.pending;
  return (
    <section className={`${wrap} page-in`} data-testid="booking-pay-page">
      <div className="max-w-xl mx-auto px-6">
        <p className="lab text-gw-accent">Wolfe Realtors · Shortlet booking</p>
        <h1 className="lux gw-h2 mt-4 inline-flex items-center gap-3" data-testid="booking-status-title">
          {b.status === "paid" && <CheckCircle2 size={28} />}{title}
        </h1>
        <p className="gw-body mt-4 opacity-80" data-testid="booking-status-text">{text}</p>
        {checking && <p className="mt-4 inline-flex items-center gap-2 text-sm" data-testid="booking-checking"><Loader2 size={14} className="animate-spin" />Confirming your payment…</p>}
        <dl className="mt-8 border-t border-gw-rule/20" data-testid="booking-details">
          <Row k="Apartment" v={b.shortlet_title} />
          <Row k="Guest" v={b.name} />
          <Row k="Dates" v={`${b.check_in} → ${b.check_out}`} />
          <Row k="Nights · guests" v={`${b.nights} · ${b.guests}`} />
          <Row k="Total" v={<span className="lux text-2xl" data-testid="booking-pay-total">{naira(b.total)}</span>} />
        </dl>
        {b.status === "approved" && !checking && (
          <>
            {b.hold_until && <p className="text-sm mt-6 inline-flex items-center gap-2 opacity-80"><Clock size={14} />Pay by {new Date(b.hold_until).toLocaleString()}</p>}
            <PayButtons token={token} providers={b.providers} />
          </>
        )}
        <Link to={`/shortlets/${b.shortlet_id}`} className="lab mt-10 inline-block underline underline-offset-4" data-testid="booking-apartment-link">View the apartment</Link>
      </div>
    </section>
  );
}
